import { verifyIsDuplicateData } from '@/helpers/verifyIsDuplicateData'
import { useQueryClient } from '@tanstack/vue-query'
import useVuelidate from '@vuelidate/core'
import { helpers, required, requiredIf } from '@vuelidate/validators'
import { computed, reactive, ref } from 'vue'
import { useToast } from 'vue-toastification'

import { Group } from '@/interfaces/group'

import { Brand } from '../interfaces/Brand'
import { Hotel } from '../interfaces/Hotel'
import { HotelGroups } from '../interfaces/HotelGroup'
import useHotels from './useHotels'
import useSearchHotels from './useSearchHotels'

const useHotelForm = (hotel?: Hotel) => {
	const toast = useToast()
	const queryClient = useQueryClient()

	interface Form {
		name: string
		rating: number
		brand?: {
			id: number
			name: string
		}
		group?: {
			id: number
			name: string
		}
		activated?: boolean
		amadeusCode?: string
		vat: number
		ish: number
	}

	const form = reactive<Form>({
		name: hotel?.name || '',
		rating: hotel?.rating || 0,
		brand:
			hotel?.brandId ?
				{
					id: hotel?.brandId,
					name: hotel?.brandName || '',
				}
			:	undefined,
		group:
			hotel?.groupId ?
				{
					id: hotel?.groupId,
					name: hotel?.groupName || '',
				}
			:	undefined,
		activated: hotel?.isActive || false,
		amadeusCode: hotel?.amadeusCode || '',
		vat: hotel?.vat || 0,
		ish: hotel?.ish || 0,
	})

	const modalAction = ref(false)

	const actionModal = () => {
		modalAction.value = !modalAction.value
	}

	const isCoincidence = ref(false)
	const isConfirmed = ref(false)

	const {
		createHotelAsyncMutation,
		createHotelLoading,
		updateHotelAsyncMutation,
		updateHotelLoading,
	} = useHotels({
		immediate: false,
	})

	const { data, setQuery, refetch } = useSearchHotels({
		immediate: false,
		queryKey: 'search-hotels-coincidences',
	})

	const duplicateBrands = computed(() => {
		return data.value?.data.filter(
			(hotel) =>
				hotel.name.toLocaleLowerCase() === form.name.toLocaleLowerCase()
		)
	})

	const formRules = {
		name: {
			required: helpers.withMessage('El nombre es requerido', required),
		},

		rating: {
			required: helpers.withMessage('La calificación es requerida', required),
			requiredIf: helpers.withMessage(
				'La calificacion debe ser mayor a 0',
				(value: number) => {
					return value > 0
				}
			),
		},
		vat: {
			required: helpers.withMessage('El IVA es requerido', required),
		},
		ish: {
			required: helpers.withMessage('El ISH es requerido', required),
		},
	}

	const formVuelidate = useVuelidate(formRules, form)

	const handleEvaluation = async () => {
		await verifyIsDuplicateData({
			data: data.value?.data!,
			form: form,
			setQuery,
			refetch: async () => {
				await refetch()
			},
			formVuelidate,
			duplicateItems: duplicateBrands,
			toast,
			handleCreate,
			isConfirmed,
			isCoincidence,
		})
	}

	const handleCreate = async () => {
		isConfirmed.value = true

		const data = await createHotelAsyncMutation({
			brandId: form.brand?.id!,
			groupId: form.group?.id!,
			name: form.name,
			rating: form.rating,
			vat: form.vat,
			ish: form.ish,
		})

		if (data) {
			actionModal()
			clearState()

			await queryClient.invalidateQueries(['brands-hotel'])
			await queryClient.invalidateQueries(['groups-hotel'])
			await queryClient.invalidateQueries(['hotels', 'list-hotels'])
			await queryClient.invalidateQueries([
				'hotels',
				'search-hotels-coincidences',
			])
		}
	}

	const handleEvaluationUpdate = async () => {
		for (const field of formVuelidate.value.$silentErrors) {
			if (!Array.isArray(field.$message)) {
				toast.error(field.$message)
				return
			}
		}

		isConfirmed.value = true

		setQuery(form.name?.toLocaleLowerCase())
		await refetch()

		if (data.value?.data.length && form.name !== hotel?.name) {
			isCoincidence.value = true
		} else {
			await handleUpdate()
		}
	}

	const handleUpdate = async () => {
		isConfirmed.value = true

		const data = await updateHotelAsyncMutation({
			brandId: form.brand?.id!,
			groupId: form.group?.id!,
			id: hotel?.id!,
			name: form.name,
			rating: form.rating,
			ish: form.ish,
			vat: form.vat,
		})

		if (data) {
			isCoincidence.value = false

			await queryClient.invalidateQueries(['hotel', hotel?.id])
			await queryClient.invalidateQueries([
				'hotels',
				'search-hotels-coincidences',
			])
		}
	}

	const clearState = () => {
		form.name = ''
		form.rating = 0
		form.brand = undefined
		form.group = undefined
		form.amadeusCode = undefined
		isConfirmed.value = false
		isCoincidence.value = false
	}

	return {
		form,
		isCoincidence,
		isConfirmed,
		modalAction,
		handleEvaluation,
		handleCreate,
		clearState,
		actionModal,
		hotels: data,
		createHotelLoading,

		handleEvaluationUpdate,
		handleUpdate,
		updateHotelLoading,
	}
}

export default useHotelForm
